// FieldGroup.tsx
import React, { useEffect } from "react";
import { Checkbox } from "@/components/ui/checkbox";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { usePublicationsInfinite } from "../../api/source.queries";
import { useSourceBasedStore } from "../../store/sourceStore";
import type { SubcategoryConfig } from "../../types/source.types";
import { SubFieldList } from "./SourceSubFieldList";
import { ViewMoreButton } from "./SourceViewMoreButton";

type Props = {
  subcat: SubcategoryConfig;
  search?: string;
};

export function FieldGroup({ subcat, search }: Props) {
  const expandedGroups = useSourceBasedStore((s) => s.expandedGroups);
  const toggleExpanded = useSourceBasedStore((s) => s.toggleExpanded);
  const selectedPublications = useSourceBasedStore(
    (s) => s.selectedPublications,
  );
  const togglePublication = useSourceBasedStore((s) => s.togglePublication);
  const setManyPublications = useSourceBasedStore((s) => s.setManyPublications);
  const initializedSubcategories = useSourceBasedStore(
    (s) => s.initializedSubcategories,
  );
  const markSubcategoryInitialized = useSourceBasedStore(
    (s) => s.markSubcategoryInitialized,
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
  } = usePublicationsInfinite(subcat, search);

  // flatten pages
  const items = React.useMemo(
    () => (data?.pages ?? []).flatMap((p) => p.items),
    [data],
  );

  // Get first page items only
  const firstPageItems = React.useMemo(
    () => data?.pages?.[0]?.items ?? [],
    [data?.pages],
  );

  useEffect(() => {
    if (
      isLoading ||
      initializedSubcategories.has(subcat.id) ||
      search?.trim() ||
      firstPageItems.length === 0
    ) {
      return;
    }

    // Auto-select all items from the first page
    setManyPublications(
      firstPageItems.map((i) => ({
        id: i.id,
        name: i.name,
        category: i.category,
        subcategory: i.subcategory,
      })),
    );

    // Mark this subcategory as initialized
    markSubcategoryInitialized(subcat.id);
  }, [
    firstPageItems,
    isLoading,
    initializedSubcategories,
    subcat.id,
    search,
    setManyPublications,
    markSubcategoryInitialized,
  ]);

  const isSearching = !!search?.trim() && (isFetching || isLoading);

  // Hide the entire field group if there are no items when searching and not currently searching
  const shouldHideGroup = search?.trim() && items.length === 0 && !isSearching;

  // Group checkbox state
  const allSelected =
    items.length > 0 &&
    items.every((i) => selectedPublications.some((p) => p.id === i.id));
  const someSelected = items.some((i) =>
    selectedPublications.some((p) => p.id === i.id),
  );

  const toggleAllVisible = (checked: boolean) => {
    if (!items.length) return;

    if (checked) {
      setManyPublications(
        items.map((i) => ({
          id: i.id,
          name: i.name,
          category: i.category,
          subcategory: i.subcategory,
        })),
      );
    } else {
      const visibleSet = new Set(items.map((i) => i.id));
      useSourceBasedStore
        .getState()
        .clearPublications((p) => visibleSet.has(p.id));
    }
  };

  // Collapse by default if searching and no items matched, otherwise use store state (defaults to true)
  const expanded =
    search?.trim() && items.length === 0
      ? false
      : expandedGroups[subcat.id] !== undefined
        ? expandedGroups[subcat.id]
        : true; // Default to expanded if not set

  if (shouldHideGroup) return null;

  return (
    <div className="flex flex-col overflow-x-hidden">
      {/* Header row */}
      <div className="flex items-center border-gray-200 bg-transparent">
        <Checkbox
          id={subcat.id}
          checked={allSelected}
          indeterminate={!allSelected && someSelected}
          onCheckedChange={(c) => toggleAllVisible(!!c)}
          className="mr-3 flex-shrink-0"
          disabled={isSearching}
        />

        <div
          onClick={() => toggleExpanded(subcat.id)}
          className="flex w-full min-w-0 items-center justify-between text-left"
        >
          <span className="text-md flex-shrink-0 cursor-pointer pr-2 font-bold text-neutral-800">
            {subcat.label}
          </span>
          <div className="h-0.5 flex-1 bg-neutral-200" />
          <div className="flex-shrink-0">
            {expanded ? (
              <Tooltip>
                <TooltipTrigger>
                  <i className="ri-arrow-up-s-fill cursor-pointer text-lg text-neutral-800"></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Collapse</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <Tooltip>
                <TooltipTrigger>
                  <i className="ri-arrow-down-s-fill cursor-pointer text-lg text-neutral-800"></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Expand</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div
        className={`overflow-hidden pt-4 pl-6 sm:pl-10 ${expanded ? "block" : "hidden"} `}
      >
        {isSearching ? (
          <div className="text-sm text-neutral-500">Searching…</div>
        ) : isLoading ? (
          <div className="text-sm text-neutral-500">Loading…</div>
        ) : (
          <>
            <SubFieldList
              items={items}
              selectedIds={new Set(selectedPublications.map((p) => p.id))}
              onToggle={(id) => {
                const item = items.find((i) => i.id === id);
                if (item) {
                  togglePublication({
                    id: item.id,
                    name: item.name,
                    category: item.category,
                    subcategory: item.subcategory,
                  });
                }
              }}
              search={search}
            />

            <div className="mt-3 flex w-full flex-row justify-between gap-2">
              {/* <ViewMoreButton
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage || isFetching}
                onClick={() => fetchNextPage()}
              /> */}

              {/* Action buttons
              {expanded && (
                <div className="flex gap-2">
                  <Button
                    onClick={() => toggleAllVisible(true)}
                    variant="minimal"
                    className="text-xs"
                    disabled={items.length === 0}
                  >
                    Select All
                  </Button>
                  <Button
                    onClick={() => clearPublicationsBySubcategory(subcat.id)}
                    variant="destructive"
                    className="text-xs"
                    disabled={items.length === 0}
                  >
                    Clear All
                  </Button>
                </div>
              )} */}

              {subcat.id === "online-news" ? (
                <a
                  href="mailto:support@yourdomain.com?subject=Request%20for%20full%20list%20of%20online%20news%20sources"
                  className="text-muted w-full cursor-pointer rounded-[8px] bg-neutral-100 p-[6px] text-center text-xs leading-normal font-bold transition-colors hover:bg-neutral-200"
                >
                  Email us to view full list
                </a>
              ) : (
                <ViewMoreButton
                  hasNextPage={hasNextPage}
                  isFetchingNextPage={isFetchingNextPage || isFetching}
                  onClick={() => fetchNextPage()}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
